// Thin JSON helpers over Inertia v3's built-in XhrHttpClient for the plain
// JSON endpoints (status poll, slug check, project create/replace). Never
// drive these with router.post/useForm — a non-Inertia JSON response would
// open Inertia's error modal (KTD2).
//
// XSRF wiring is automatic: the client mirrors the `XSRF-TOKEN` cookie into
// the `X-XSRF-TOKEN` header on every request, and inertia_rails' middleware
// bridges that to Rails' CSRF check — forgery protection stays ON.
import { http } from '@inertiajs/react'
import type { Method } from '@inertiajs/core'

const LOGIN_PATH = '/session/new'

/** A JSON request that reached the server but came back >= 400. */
export class JsonRequestError extends Error {
  readonly status: number
  readonly body: string

  constructor(status: number, body: string, url: string) {
    super(`JSON request to ${url} failed with status ${status}`)
    this.name = 'JsonRequestError'
    this.status = status
    this.body = body
  }
}

interface ResponseErrorLike {
  response: { status: number; data: string }
}

function isResponseError(error: unknown): error is ResponseErrorLike {
  if (typeof error !== 'object' || error === null) return false
  const response = (error as { response?: unknown }).response
  return (
    typeof response === 'object' &&
    response !== null &&
    typeof (response as { status?: unknown }).status === 'number'
  )
}

async function requestJson<T>(
  method: Method,
  url: string,
  data?: unknown,
  params?: Record<string, unknown>,
): Promise<T> {
  try {
    const response = await http.getClient().request({
      method,
      url,
      data,
      params,
      // Explicit Accept: the browser default (*/*) routes an expired session
      // into the HTML branch instead of the JSON 401 contract.
      headers: { Accept: 'application/json' },
    })
    return (response.data === '' ? undefined : JSON.parse(response.data)) as T
  } catch (error) {
    if (isResponseError(error)) {
      if (error.response.status === 401) {
        // Session expired: leave the SPA for the ERB login screen (KTD12).
        window.location.assign(LOGIN_PATH)
      }
      throw new JsonRequestError(
        error.response.status,
        error.response.data,
        url,
      )
    }
    throw error // network failure / cancellation — the caller decides
  }
}

export function getJson<T>(
  url: string,
  params?: Record<string, unknown>,
): Promise<T> {
  return requestJson<T>('get', url, undefined, params)
}

export function postJson<T>(url: string, data?: unknown): Promise<T> {
  return requestJson<T>('post', url, data)
}

/** JSON.parse guarded by a fallback — for 422 bodies that may not be JSON. */
export function safeJsonParse<T>(text: string, fallback: T): T {
  try {
    return JSON.parse(text) as T
  } catch {
    return fallback
  }
}

/** Rails-style %{var} substitution for templates arriving via `strings`. */
export function interpolate(
  template: string,
  vars: Record<string, string | number>,
): string {
  return template.replace(/%\{(\w+)\}/g, (match, key: string) =>
    Object.prototype.hasOwnProperty.call(vars, key) ? String(vars[key]) : match,
  )
}

/**
 * Copy `text` to the clipboard. Secure contexts use navigator.clipboard;
 * insecure dev origins (http://app.lvh.me:3000) lack it, so a hidden-textarea
 * execCommand('copy') fallback covers them. Resolves false when neither works.
 */
export async function copyToClipboard(text: string): Promise<boolean> {
  if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text)
      return true
    } catch {
      // fall through to the legacy path
    }
  }

  const textarea = document.createElement('textarea')
  textarea.value = text
  textarea.setAttribute('readonly', '')
  textarea.style.position = 'fixed'
  textarea.style.top = '0'
  textarea.style.left = '-9999px'
  document.body.appendChild(textarea)
  textarea.select()
  let copied: boolean
  try {
    copied = document.execCommand('copy')
  } catch {
    copied = false
  }
  textarea.remove()
  return copied
}
